import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // Import useLocation
import { useForm, Controller } from 'react-hook-form';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AsyncSelect from 'react-select/async'; // Using react-select/async for client dropdown

interface ContactFormData {
  name: string;
  phone: string;
  email?: string;
  brand?: string;
  clientId?: number | null; // Client ID is optional
}

interface ClientOption {
  value: number;
  label: string;
}

// Helper function to format E.164 number to +x (xxx) xxx-xx-xx
// Basic implementation, focusing on +7 KZ format
const formatPhoneNumber = (e164Number: string): string => {
  // Remove the leading '+' and any non-digits
  const digits = e164Number.replace(/\D/g, '');

  // Assuming KZ format (+7 XXX XXX XX XX) -> 11 digits after '+'
  if (digits.length === 11 && e164Number.startsWith('+7')) {
    // Format: +7 (XXX) XXX-XX-XX
    return `+${digits[0]} (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 9)}-${digits.substring(9, 11)}`;
  }
  // Add formatting for other country codes if needed here
  // Example for a generic +1 number (US/Canada)
  // else if (digits.length === 11 && e164Number.startsWith('+1')) {
  //   return `+${digits[0]} (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
  // }

  // Fallback: return the original E.164 number if formatting rules don't match
  return e164Number;
};


const ContactForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    control, // Need control for react-select
    formState: { errors, isDirty }
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      phone: '',
      brand: '',
      email: '',
      clientId: null
    }
  });

  const location = useLocation(); // Get location object

  // Add a new useEffect hook to handle query parameters
  useEffect(() => {
    if (!isEditMode) {
        const searchParams = new URLSearchParams(location.search);
        const phoneParam = searchParams.get('phone');
        if (phoneParam) {
            const decodedPhone = decodeURIComponent(phoneParam);
            
            let phoneToFormat = decodedPhone;
            if (decodedPhone.includes('@lid')) {
                const phoneMatch = decodedPhone.match(/(\d{10,})/);
                if (phoneMatch) {
                    phoneToFormat = phoneMatch[1];
                } else {
                    phoneToFormat = decodedPhone;
                }
            }
            
            const formattedPhone = formatPhoneNumber(phoneToFormat);
            setValue('phone', formattedPhone);
        }
    }
}, [isEditMode, location.search, setValue]);


  // Fetch client options for dropdown
  useEffect(() => {
    axios.get(`/clients/options?page=1&limit=15`) // Assuming this endpoint exists from ClientForm
      .then(response => {
        const options = response.data;
        setClientOptions(options);
      })
      .catch(err => {
        console.error('Error fetching client options:', err);
        // Handle error appropriately, maybe show a message
      });
  }, []);


  // Fetch existing contact data in edit mode
  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      axios.get(`/contacts/${id}`) // Assuming this endpoint exists
        .then(response => {
          const contact = response.data;
          setValue('name', contact.name);
          setValue('phone', contact.phone);
          setValue('brand', contact.brand || '');
          setValue('email', contact.email || '');
          setValue('clientId', contact.clientId || null); // Set clientId
        })
        .catch(err => {
          console.error('Error fetching contact:', err);
          setError('Ошибка при загрузке данных контакта');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEditMode, setValue]);

  const onSubmit = async (data: ContactFormData) => {
    setSubmitting(true);
    setError(null);

    // Ensure clientId is number or null, not empty string if unselected
    const payload = {
      ...data,
      clientId: data.clientId || null
    };

    try {
      if (isEditMode) {
        await axios.put(`/contacts/${id}`, payload);
      } else {
        await axios.post('/contacts', payload);
      }
      navigate('/contacts'); // Navigate back to contacts list
    } catch (err: any) {
      console.error('Error saving contact:', err);
      setError(err.response?.data?.message || 'Ошибка при сохранении контакта');
    } finally {
      setSubmitting(false);
    }
  };

  const loadOptions = async (inputValue: string): Promise<ClientOption[]> => {
    try {
      const response = await axios.get(`/clients/options?page=1&limit=15&search=${inputValue || ''}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching client options:', err);
      return [];
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title={isEditMode ? 'Редактировать контакт' : 'Добавить контакт'}
        backLink="/contacts"
      />

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="form-label">
              Имя *
            </label>
            <input
              id="name"
              type="text"
              className={`form-input ${errors.name ? 'border-red-500' : ''}`}
              placeholder="Имя контакта"
              {...register('name', { required: 'Имя обязательно' })}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="form-label">
              Телефон *
            </label>
            <input
              id="phone"
              type="text"
              className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
              placeholder="+7 (XXX) XXX-XX-XX"
              {...register('phone', {
                required: 'Телефон обязателен',
                pattern: {
                  value: /^[+]?[0-9\s()-]{10,17}$/,
                  message: 'Введите корректный номер телефона'
                }
              })}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="form-input"
              placeholder="Email (необязательно)"
              {...register('email')}
            />
          </div>
          
          {/* Brand */}
          <div>
            <label htmlFor="brand" className="form-label">
              Бренд
            </label>
            <input
              id="brand"
              type="text"
              className="form-input"
              placeholder="Название бренда (необязательно)"
              {...register('brand')}
            />
          </div>

          {/* Client Link */}
          <div>
            <label htmlFor="clientId" className="form-label">
              Связанный клиент
            </label>
            <Controller
              name="clientId"
              control={control}
              render={({ field }) => (
                <AsyncSelect<ClientOption>
                  inputId="clientId"
                  defaultOptions={clientOptions}
                  loadOptions={loadOptions}
                  value={clientOptions.find(c => (c.value + '').toLowerCase() === (field.value + '').toLowerCase())}
                  onChange={option => field.onChange(option ? option.value : null)}
                  isClearable
                  placeholder="Выберите клиента..."
                  classNamePrefix="react-select" // for potential custom styling
                  styles={{ // Basic styling adjustment
                    control: (base) => ({
                      ...base,
                      borderColor: '#d1d5db', // gray-300
                      '&:hover': { borderColor: '#9ca3af' } // gray-400
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: isSelected ? '#10b981' : isFocused ? '#d1fae5' : undefined, // emerald-500, emerald-100
                      color: isSelected ? 'white' : 'black',
                      ':active': {
                        ...base[':active'],
                        backgroundColor: isSelected ? '#059669' : '#a7f3d0', // emerald-600, emerald-200
                      },
                    }),
                  }}
                />
              )}
            />
            {/* No error display needed for optional field usually */}
          </div>


          <div className="pt-5">
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={() => navigate('/contacts')} className="btn-secondary" disabled={submitting}>
                Отмена
              </button>
              <button type="submit" className="btn-primary" disabled={submitting || !isDirty}>
                {submitting ? <LoadingSpinner /> : (isEditMode ? 'Сохранить изменения' : 'Добавить контакт')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
